import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { errMsg, useApi } from "@/lib/api";
import { PesertaFeedback } from "@/components/program/PesertaFeedback";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { tarikh: today(), bilangan: "", berjaya: "", kurang: "", maklum_balas: "", perbaiki: "" };
const TEXT = [["berjaya", "Apa yang berjaya"], ["kurang", "Apa yang kurang berkesan"], ["maklum_balas", "Maklum balas peserta"], ["perbaiki", "Apa yang perlu diperbaiki"]];
const inp = "w-full px-3 rounded-xl bg-bg border border-line text-sm focus:outline-none focus:border-[var(--q)]";

const AarForm = ({ init, onSave, onCancel, busy }) => {
  const [f, setF] = useState(init);
  return (
    <div className="surface rounded-2xl p-5 space-y-3" data-testid="aar-form">
      <div className="grid grid-cols-2 gap-3">
        <label><span className="eyebrow block mb-1">Tarikh program</span><input type="date" value={f.tarikh} onChange={(e) => setF({ ...f, tarikh: e.target.value })} className={`${inp} h-10`} data-testid="aar-tarikh" /></label>
        <label><span className="eyebrow block mb-1">Bilangan peserta</span><input type="number" min="0" value={f.bilangan} onChange={(e) => setF({ ...f, bilangan: e.target.value })} className={`${inp} h-10`} data-testid="aar-bilangan" /></label>
      </div>
      {TEXT.map(([k, l]) => (
        <label key={k} className="block"><span className="eyebrow block mb-1">{l}</span>
          <textarea rows={2} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={`${inp} py-2 resize-y`} data-testid={`aar-${k}`} /></label>
      ))}
      <div className="flex gap-2">
        <button onClick={() => onSave({ ...f, bilangan: Number(f.bilangan || 0) })} disabled={busy || !f.tarikh} data-testid="aar-save-button"
          className="h-10 px-5 rounded-full bg-ink text-bg text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-40">{busy && <Loader2 size={14} className="animate-spin" />} Simpan AAR</button>
        <button onClick={onCancel} className="h-10 px-4 rounded-full border border-line text-sm" data-testid="aar-cancel-button">Batal</button>
      </div>
    </div>
  );
};

export const AarPanel = ({ kajian, onChange }) => {
  const api = useApi();
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const items = kajian.aar || [];
  const save = async (f) => {
    setBusy(true);
    try {
      if (edit === "new") { const it = await api("post", `/hte/kajian/${kajian.id}/aar`, f); onChange({ aar: [...items, it], status: "selesai" }); }
      else { await api("put", `/hte/kajian/${kajian.id}/aar/${edit.id}`, f); onChange({ aar: items.map((x) => (x.id === edit.id ? { ...x, ...f } : x)) }); }
      toast.success("AAR disimpan"); setEdit(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Padam AAR ${a.tarikh}?`)) return;
    try { await api("delete", `/hte/kajian/${kajian.id}/aar/${a.id}`); onChange({ aar: items.filter((x) => x.id !== a.id) }); toast.success("AAR dipadam"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-4" data-testid="aar-panel">
      <p className="text-sm text-mute">Refleksi & Penambahbaikan (AAR) selepas program. Rekod ini percuma dan digunakan semasa membina versi seterusnya.</p>
      {items.map((a) => (
        <div key={a.id} className="rounded-2xl border border-line p-4 text-sm space-y-1" data-testid={`aar-item-${a.id}`}>
          <div className="flex justify-between gap-2"><strong>{a.tarikh} · {a.bilangan} peserta</strong>
            <span className="flex gap-1">
              <button onClick={() => setEdit(a)} aria-label="Sunting AAR" data-testid={`aar-edit-${a.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-surface"><Pencil size={14} /></button>
              <button onClick={() => remove(a)} aria-label="Padam AAR" data-testid={`aar-delete-${a.id}`} className="h-8 w-8 grid place-items-center rounded-full hover:bg-warn-soft hover:text-warn"><Trash2 size={14} /></button>
            </span></div>
          {TEXT.map(([k, l]) => a[k] && <p key={k}><span className="text-mute">{l}:</span> {a[k]}</p>)}
        </div>
      ))}
      <div className="pt-4 border-t border-line space-y-3">
        <div className="eyebrow">Maklum balas peserta</div>
        <PesertaFeedback kajian={kajian} onChange={onChange} />
      </div>
      {edit ? <AarForm init={edit === "new" ? EMPTY : edit} onSave={save} onCancel={() => setEdit(null)} busy={busy} /> : (
        <button onClick={() => setEdit("new")} data-testid="aar-add-button" className="h-10 px-5 rounded-full border border-line text-sm inline-flex items-center gap-2 hover:bg-surface"><Plus size={14} /> Catat AAR</button>
      )}
    </section>
  );
};
